import { h } from 'preact';
import Store from 'store/store';

import * as jsonPointer from 'utils/jsonPointer';
import { confirm } from 'utils/modal';
import { parseDate } from 'utils/utils';
import formatDateTime from 'utils/formatDateTime';
import { getButtons } from 'components/editor/pages/landing/misc';
import { canUser } from 'utils/roles';

import HubPageEditor from 'components/editor/pages/hubPageEditor';
import SelectableSection from 'components/editor/selectableSection';
import SimpleField from 'components/editor/settings/simpleField';
import CarouselSettings from 'components/editor/settings/carouselSettings/carouselSettings';
import Hero from 'components/editor/pages/landing/hero/hero.js';
import MoviesList from 'components/editor/pages/landing/scrollList/moviesList';
import PromoTouts from 'components/editor/pages/landing/promoTouts/promoTouts';
import Button from 'components/button/button';
import HubNavigation from 'components/editor/hubNavigation/hubNavigation';
import HubFooter from 'components/editor/hubFooter/hubFooter';
import MediaField from 'components/editor/settings/mediaField';
import { Sortable, SortableItem, Handle } from 'components/sortable/sortable';
import LoadingBar from 'components/loadingBar/loadingBar';

import Alert from 'components/icons/alert';

import s from 'components/editor/pages/landing.sss';

export default class LandingEditor extends HubPageEditor {
	constructor(props) {
		super(props);

		this.state = {
			readyState: 'loading',
			movies: {},
		};

		this.addPromoTout = this.addPromoTout.bind(this);
		this.onPromoToutSort = this.onPromoToutSort.bind(this);
		this.editPromoTout = this.editPromoTout.bind(this);
		this.onDeletePromoToutClick = this.onDeletePromoToutClick.bind(this);
		this.renderPromoToutItem = this.renderPromoToutItem.bind(this);
	}

	componentWillMount() {
		super.componentWillMount?.();
		this.prepareData();
	}

	async prepareData() {
		await this.updateMovies();
		this.updateContent();
		this.setState({ readyState: 'loaded' });
	}

	async updateMovies() {
		await Store.get().publishedDataPromise;

		const publishedMovies = Store.get().publishedData?.movies || {};
		const movies = Object.fromEntries(
			Object.entries(publishedMovies).filter(([, movie]) => movie?.thundr_id && movie?.onesheet?.url)
		);

		this.setState({ movies });
	}

	updateContent() {
		if (!this.props.dataRoot || !canUser('write')) return;
		const content = this.computeContent();
		jsonPointer.get(Store.get().data, jsonPointer.parse(this.props.dataRoot).concat('content'))?.reset(content);
	}

	computeContent() {
		const basePath = this.props.dataRoot || '';
		const liveData = jsonPointer.get(Store.get().data, basePath)?.content;
		const heroList = (liveData?.hero?.list || []).filter(e => e.movie && this.state.movies[e.movie]);

		return {
			hero: { list: heroList },
			carousels: liveData?.carousels || {},
			customCarousels: liveData?.customCarousels || [],
			promoTouts: liveData?.promoTouts || {},
		};
	}

	selectParentLevel() {
		const activeElement = this.props.activeElement;
		if (!activeElement) return;
		const path = jsonPointer.parse(activeElement);
		path.pop();
		let newPath = !path.length ? null : jsonPointer.compile(path);
		this.props.select(newPath);
	}

	checkScrollTop(e) {
		const el = e.target;
		if (el.scrollTop > 0) {
			el.dataset.scrolled = '';
		} else {
			delete el.dataset.scrolled;
		}
	}

	getAutoCarouselItems() {
		const today = formatDateTime(new Date(), 'yyyy-MM-dd');
		const content = this.props.content;

		return Object.values(this.state.movies || {}).sort(this.sortMoviesByClosestReleaseDate).reduce((acc, movie) => {
			if (movie.placeholder) return acc;
			if (!movie?.onesheet?.url) return acc; // temp: only show movies with a onesheet
			if (movie.dates?.hideFromCarouselsAfter && today >= movie.dates.hideFromCarouselsAfter) return acc;

			const overrides = content?.carousels?.overrides?.[movie.id];

			const status = movie.status;
			let type;
			if (!status) {
				type = today >= movie.dates?.release ? 'inRelease' : 'comingSoon';
			} else if (status.theaters === 'comingSoon' || status.theaters === 'preSale') {
				type = 'comingSoon';
			} else if (status.theaters === 'inTheaters' || status.homeEnt === 'homeEnt') {
				type = 'inRelease';
			}
			if (!type) return acc;

			const pinned = content?.carousels?.[type]?.pinned === movie.id;
			const data = { movie, pinned, ...overrides };
			acc[type][data.pinned ? 'unshift' : 'push']({ movie, pinned, ...overrides });
			return acc;
		}, { inRelease: [], comingSoon: [] });
	}

	renderVisual() {
		if (this.state.readyState === 'loading') {
			return (
				<div class={s.visual}>
					<HubNavigation />
					<LoadingBar class={s.loadingBar} />
				</div>
			);
		}

		const liveData = this.props.content;

		const { inRelease, comingSoon } = this.getAutoCarouselItems();
		const promoTouts = Object.values(liveData?.promoTouts?.list || {}).sort(this.sortItemsByOrder);

		return (
			<div class={s.visual} onScroll={this.checkScrollTop}>
				<HubNavigation />
				<SelectableSection section="/hero" class={s.page} {...this.props}>
					<Hero list={liveData?.hero?.list} movies={this.state.movies} activeElement={this.props.activeElement} />
				</SelectableSection>
				<SelectableSection section="/inRelease" class={s.page} {...this.props}>
					<MoviesList
						id="in-release"
						title={this.getCopy('$LANDING_IN_RELEASE', 'In\nRelease')}
						items={inRelease}
						basePath="/inRelease"
						activeElement={this.props.activeElement}
						automatic
						key="in-release"
					/>
				</SelectableSection>
				<SelectableSection section="/comingSoon" class={s.page} {...this.props}>
					<MoviesList
						id="coming-soon"
						title={this.getCopy('$LANDING_COMING_SOON', 'Coming\nSoon')}
						items={comingSoon}
						basePath="/comingSoon"
						activeElement={this.props.activeElement}
						automatic
						key="coming-soon"
					/>
				</SelectableSection>
				<SelectableSection section="/promoTouts" class={s.page} {...this.props}>
					<PromoTouts
						id="promo-touts"
						basePath="/promoTouts"
						items={promoTouts}
						activeElement={this.props.activeElement}
						key="promo-touts"
					/>
				</SelectableSection>
				<HubFooter />
			</div>
		);
	}

	addMovieToCarousel(e) {
		const movieId = e.target.closest('[data-movie]')?.dataset.movie;
		const carousel = e.target.closest('[data-carousel]')?.dataset.carousel;
		if (!carousel || !movieId) return;

		const data = { movie: movieId };
		const carouselContent = jsonPointer.get(Store.get().data, `${this.props.dataRoot || ''}/content/${carousel}`);
		if (!carouselContent.list) {
			carouselContent.set('list', [data]);
		} else {
			carouselContent.list.push(data);
		}
	}

	removeMovieFromCarousel(e) {
		const movieId = e.target.closest('[data-movie]')?.dataset.movie;
		const carousel = e.target.closest('[data-carousel]')?.dataset.carousel;
		if (!carousel || !movieId) return;

		const list = jsonPointer.get(Store.get().data, `${this.props.dataRoot || ''}/content/${carousel}/list`);
		const index = list?.findIndex(item => item.movie === movieId);
		if (!list || index === -1) return;
		list.splice(index, 1);
	}

	editCarouselMovie(e) {
		const movieId = e.target.closest('[data-movie]')?.dataset.movie;
		const carousel = e.target.closest('[data-carousel]')?.dataset.carousel;
		if (!carousel || !movieId) return;

		if (carousel === 'hero') {
			const list = jsonPointer.get(Store.get().data, `${this.props.dataRoot || ''}/content/${carousel}/list`);
			const index = list?.findIndex(item => item.movie === movieId);
			if (!list || index === -1) return;
			this.props.select(`/${carousel}/list/${index}`);
		} else {
			this.props.select(`/carousels/overrides/${movieId}`);
		}
	}

	onCarouselSort(e) {
		const carousel = e.target.closest('[data-carousel]')?.dataset.carousel;
		if (!carousel) return;

		// Do we guarantee that this callback is called before the dom update?
		// const allItems = [...e.target.querySelectorAll('[data-movie]')].map(e => e.dataset.movie);
		// allItems.splice(e.to, 0, allItems.splice(e.from, 1)[0]);

		let basePath = this.props.dataRoot || '';
		const carouselPath = `${basePath}/content/${carousel}`;
		const parent = jsonPointer.get(Store.get().data, `${carouselPath}`);
		const list = parent.list?.toJS() || [];
		list.splice(e.to, 0, list.splice(e.from, 1)[0]);
		parent.set('list', list);
	}

	togglePinned(e) {
		const movieId = e.target.closest('[data-movie]')?.dataset.movie;
		const carousel = e.target.closest('[data-carousel]')?.dataset.carousel;
		if (!carousel || !movieId) return;

		const path = `${this.props.dataRoot || ''}/content/carousels/${carousel}/pinned`;
		const pinned = jsonPointer.get(Store.get().data, path) !== movieId;
		jsonPointer.setDeep(Store.get().data, path, pinned ? movieId : null);
	}

	sortItemsByOrder(a, b) {
		return (b?.pinned || false) - (a?.pinned || false) || (a?.order || 0) - (b?.order || 0);
	}

	sortMoviesByClosestReleaseDate(a, b) {
		// movies no longer in cinemas always go last
		const aStatus = a.status;
		const bStatus = b.status;

		const aTheaters = aStatus && aStatus.theaters !== 'unknown';
		const bTheaters = bStatus && bStatus.theaters !== 'unknown';
		if (aTheaters !== bTheaters) return aTheaters ? -1 : 1;

		// Closest release date first (future or past)
		const ra = parseDate(a?.dates?.release) || null;
		const rb = parseDate(b?.dates?.release) || null;
		if (!ra && !rb) return 0;
		if (!ra) return 1;
		if (!rb) return -1;
		const now = Date.now();
		return Math.abs(now - ra) - Math.abs(now - rb);
	}

	renderCarouselItemSettings(movieData) {
		if (!this.props.activeElement) {
			return;
		}
		let basePath = `${this.props.dataRoot || ''}/content${this.props.activeElement}`;
		const [defaultPrimaryButton, defaultSecondaryButton] = getButtons(movieData, undefined, this.state.thundrData);
		// if (/^youtube:/.test(defaultSecondaryButton?.destination)) defaultSecondaryButton.destination = '(trailer)';
		return (
			<div class={s.itemEditor}>
				<h3>Primary Button</h3>
				<SimpleField dark path={`${basePath}/primaryButtonText`} placeholder={defaultPrimaryButton?.label} label="Primary button text" help="Overrides the primary button text" />
				<SimpleField dark path={`${basePath}/primaryButtonLink`} placeholder={defaultPrimaryButton?.destination} label="Primary button Link" help="Overrides the primary button link" />
				<h3>Secondary Button</h3>
				<SimpleField dark path={`${basePath}/secondaryButtonText`} placeholder={defaultSecondaryButton?.label} label="Secondary button text" help="Overrides the secondary button text" />
				<SimpleField dark path={`${basePath}/secondaryButtonLink`} placeholder={defaultSecondaryButton?.destination} label="Secondary button link" help="Overrides the secondary button link" />
			</div>
		);
	}

	async addPromoTout() {
		const basePath = `${this.props.dataRoot || ''}/content/promoTouts/list`;
		const currentTouts = jsonPointer.get(Store.get().data, basePath) || {};

		const toutCount = Object.keys(currentTouts).length;
		if (toutCount >= 6) {
			await confirm('You cannot add more than 6 Promo Touts.', {
				title: 'Limit reached',
				button: 'OK',
				danger: false,
			});
			return;
		}

		const id = crypto.randomUUID();

		const existingOrders = new Set(
			Object.values(currentTouts).map(tout => +tout.order || 0)
		);
		let order = 1;
		while (existingOrders.has(order)) {
			order++;
		}

		const newTout = {
			id,
			order,
			title: `Tout ${order}`,
			image: '',
			link: '',
			alt: `Promo Tout ${order} Image`
		};

		jsonPointer.setDeep(Store.get().data, `${basePath}/${id}`, newTout);
		this.props.select(`/promoTouts/list/${id}`);
	}


	editPromoTout(e) {
		const id = e.target.closest('[data-tout]')?.dataset.tout;
		if (!id) return;
		this.props.select(`/promoTouts/list/${id}`);
	}

	async onDeletePromoToutClick() {
		const ok = await confirm('This will delete the selected Promo Tout item and cannot be undone', { title: 'Delete item?', danger: true, button: 'Delete item' });
		if (!ok) return;
		this.deletePromoTout();
	}

	deletePromoTout() {
		if (!this.props.activeElement || !jsonPointer.has(this.props.content, this.props.activeElement)) {
			return;
		}
		const path = jsonPointer.parse(`${this.props.dataRoot || ''}/content${this.props.activeElement}`);
		const key = path.pop();
		const parent = jsonPointer.get(Store.get().data, path);
		parent.remove(key);
		this.props.select('/promoTouts');

	}

	onPromoToutSort(e) {
		const listContainer = e.target.closest('[data-promo-touts]');
		if (!listContainer) return;

		// Grab the reordered IDs
		const allItems = [...e.target.querySelectorAll('[data-tout]')].map(el => el.dataset.tout);

		// Move the item from its original index to the new one
		allItems.splice(e.to, 0, allItems.splice(e.from, 1)[0]);

		// Set the new order using jsonPointer
		const basePath = this.props.dataRoot || '';
		allItems.forEach((id, index) => {
			jsonPointer.set(Store.get().data, `${basePath}/content/promoTouts/list/${id}/order`, index + 1);
		});
	}

	renderPromoToutsSettings() {
		const promoTouts = this.props.content?.promoTouts?.list || {};
		const toutArray = Object.values(promoTouts);

		toutArray.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

		const readonly = !canUser('write');

		return (
			<div class={s.carouselSettings} data-promo-touts>
				<div class={s.section}>
					<Button primary class={s.add} onClick={this.addPromoTout} disabled={readonly}>Add Tout</Button>
					<Sortable class={s.list} onChange={this.onPromoToutSort} disabled={readonly}>
						{toutArray.map(this.renderPromoToutItem)}
					</Sortable>
				</div>
			</div>
		);
	}

	renderPromoToutItem(item) {
		if (!item) return null;

		return (
			<SortableItem class={s.carouselItemWrapper} data-tout={item.id}>
				<Handle class={s.handle} />
				<div class={s.carouselItemContainer}>
					<div class={s.promoToutsItemHeader}>
						<span class={s.carouselItemTitle}>
							{!item.image && <Alert title="No image selected, tout will not be displayed" />}
							{item.title || 'Tout'}
						</span>
						<div class={s.buttonRow}>
							<Button primary class={s.promoToutButton} onClick={this.editPromoTout}>
								Edit
							</Button>
						</div>
					</div>
				</div>
			</SortableItem>
		);
	}

	renderPromoToutsItemSettings() {
		if (!this.props.activeElement || !jsonPointer.has(this.props.content, this.props.activeElement)) {
			return null;
		}

		const basePath = `${this.props.dataRoot || ''}/content${this.props.activeElement}`;
		const item = jsonPointer.get(Store.get().data, basePath);
		const { title } = item || {};

		return (
			<div class={s.itemEditor}>
				<SimpleField dark path={`${basePath}/title`} label="Title" placeholder="Tout" />
				<MediaField
					dark
					label="Image"
					help={'Aspect ratio: 16:10\nRecommended dimensions: 800x500px'}
					path={`${basePath}/image`}
					group={this.props.config?.id}
					assetData={{ type: 'Promo Tout Image', aspectRatio: '16:10', recommendedDimensions: '800:500' }}
				/>
				<SimpleField dark path={`${basePath}/alt`} label="Image alt text" placeholder={`${title || 'Tout'} image`} />
				<SimpleField dark path={`${basePath}/link`} label="Link URL" />
				<Button danger onClick={this.onDeletePromoToutClick} disabled={!canUser('write')}>Delete item</Button>
			</div>
		);
	}

	renderCarouselSettings(type, automatic) {
		const movies = automatic
			? this.getAutoCarouselItems()?.[type]
			: this.state.movies;
		return (
			<CarouselSettings
				carouselId={type}
				dataRoot={this.props.dataRoot}
				inCarouselLabel={type === 'hero' && 'In Hero Carousel'}
				movies={movies}
				onItemSelect={element => this.props.select(element)}
				selectedMovies={this.props.content?.[type]?.list}
				sortable={!automatic}
			/>
		);
	}

	renderSettings() {
		let content;
		let nav;
		const activeElement = this.props.activeElement;
		const carousels = [
			{ type: 'hero', name: 'Hero Carousel' },
			{ type: 'inRelease', name: 'In Release', automatic: true },
			{ type: 'comingSoon', name: 'Coming Soon', automatic: true },
		];
		for (const carousel of carousels) {
			if (activeElement.startsWith(`/${carousel.type}`)) {
				nav = [{ label: carousel.name, path: `/${carousel.type}` }];
				if (activeElement.startsWith(`/${carousel.type}/list/`)) {
					const item = jsonPointer.get(this.props.content, activeElement);
					const movieData = typeof item?.movie === 'string' ? this.state.movies?.[item.movie] : item?.movie;
					nav.push({ label: movieData?.content?.title || movieData?.title || 'Movie', path: activeElement });
					content = this.renderCarouselItemSettings(movieData);
				} else {
					content = this.renderCarouselSettings(carousel.type, carousel.automatic);
				}
				break;
			}
		}

		if (activeElement.startsWith('/carousels/overrides/')) {
			const movieId = jsonPointer.parse(activeElement)[2];
			const movie = this.state.movies?.[movieId];
			if (!movie) return;
			const autoCarousels = this.getAutoCarouselItems();
			const carousel = carousels.find(c => autoCarousels[c.type]?.some(e => e?.movie?.id === movieId));
			nav = [
				carousel && { label: carousel.name || 'Carousel', path: `/${carousel.type}` },
				{ label: movie?.content?.title || movie?.title || 'Movie', path: activeElement }
			].filter(e => e);
			content = this.renderCarouselItemSettings(movie);
		}

		if (activeElement.startsWith(`/promoTouts`)) {
			nav = [{ label: 'Promo Touts', path: '/promoTouts' }];
			if (activeElement.startsWith(`/promoTouts/list/`)) {
				const item = jsonPointer.get(this.props.content, activeElement);
				nav.push({ label: item?.title || 'Tout', path: activeElement });
				content = this.renderPromoToutsItemSettings();
			} else {
				content = this.renderPromoToutsSettings();
			}
		}

		return (
			<div class={s.settings}>
				{this.renderSettingsNavigation(nav)}
				{content}
			</div>
		);
	}
}